import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Label } from '../../design-system';
import { resolveTenant, TenantPublicInfo } from '../../services/consentApi';
import { 
  initiatePortalLogin, 
  verifyPortalOtp, 
  verifySecurityAnswer,
  storePortalSession,
  PortalLoginInitiateResponse,
  PortalOtpVerifyResponse 
} from '../../services/portalApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

type LoginStep = 'identifier' | 'otp' | 'security';

// Responsive styles for different screen sizes
const styles = {
  // Main container - full height with gradient background
  mainContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--tashi-blue-grey-9) 0%, var(--tashi-blue-grey-7) 50%, var(--tashi-blue-grey-8) 100%)',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  // Top bar with language selector
  topBar: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  // Main content area - centers the login card
  contentArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  // Two-column layout for larger screens
  twoColumnLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '48px',
    maxWidth: '1200px',
    width: '100%',
    alignItems: 'center',
  },
  // Hero section (left side on desktop)
  heroSection: {
    display: 'none' as const,
    flexDirection: 'column' as const,
    gap: '24px',
    color: 'white',
  },
  // Login card container
  loginCardContainer: {
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
  },
};

/**
 * Data Owner Portal Login - 3-step authentication
 * Route: /t/:tenantSlug/my-consent/login
 */
const DataOwnerPortalLogin: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { t, language, setLanguage, supportedLanguages } = useI18n();
  
  // Responsive state
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  
  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tenant state
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState<string | null>(null);

  // Login flow state
  const [step, setStep] = useState<LoginStep>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  
  // Session state from API responses
  const [loginSession, setLoginSession] = useState<PortalLoginInitiateResponse | null>(null);
  const [securityQuestion, setSecurityQuestion] = useState<PortalOtpVerifyResponse | null>(null);
  
  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Resolve tenant on mount
  useEffect(() => {
    const fetchTenant = async () => {
      if (!tenantSlug) {
        setTenantError('No organization specified');
        setTenantLoading(false);
        return;
      }
      
      try {
        const tenantInfo = await resolveTenant(tenantSlug);
        setTenant(tenantInfo);
        setTenantError(null);
      } catch (err) {
        setTenantError('Organization not found. Please check the URL.');
      } finally {
        setTenantLoading(false);
      }
    };
    
    fetchTenant();
  }, [tenantSlug]);

  // OTP resend countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Step 1: Submit phone/email
  const handleIdentifierSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!identifier.trim()) {
      setError(t.portal?.enterPhoneOrEmail || 'Please enter your phone number or email');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await initiatePortalLogin({
        tenantSlug,
        identifier: identifier.trim(),
      });
      
      setLoginSession(response);
      setCountdown(response.otpValiditySeconds || 120);
      setStep('otp');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send OTP';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [identifier, tenantSlug, t]);

  // Step 2: Verify OTP
  const handleOtpSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!otp.trim() || otp.length < 4) {
      setError(t.portal?.enterValidOtp || 'Please enter a valid OTP');
      return;
    }
    
    if (!loginSession) {
      setError('Session expired. Please start again.');
      setStep('identifier');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await verifyPortalOtp({
        sessionToken: loginSession.sessionToken,
        otp: otp.trim(),
      });
      
      // Check if OTP-only mode (login complete after OTP)
      if (response.otpOnlyMode && response.accessToken) {
        // Login complete - store session and redirect
        storePortalSession({
          success: true,
          message: response.message,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken!,
          expiresIn: response.expiresIn!,
          dataOwner: response.dataOwner!,
          tenant: response.tenant!,
        });
        navigate(`/t/${tenantSlug}/my-consent/history`);
        return;
      }
      
      // Normal flow - proceed to security question
      setSecurityQuestion(response);
      setStep('security');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Invalid OTP';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [otp, loginSession, tenantSlug, navigate, t]);

  // Step 3: Verify security answer
  const handleSecuritySubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!securityAnswer.trim()) {
      setError(t.portal?.enterSecurityAnswer || 'Please enter your answer');
      return;
    }
    
    if (!securityQuestion?.verificationToken) {
      setError('Session expired. Please start again.');
      setStep('identifier');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await verifySecurityAnswer({
        verificationToken: securityQuestion.verificationToken,
        answer: securityAnswer.trim(),
      });
      
      storePortalSession(response);
      navigate(`/t/${tenantSlug}/my-consent/history`);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Incorrect answer';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [securityAnswer, securityQuestion, tenantSlug, navigate, t]);

  // Resend OTP
  const handleResendOtp = useCallback(async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await initiatePortalLogin({
        tenantSlug,
        identifier: identifier.trim(),
      });
      
      setLoginSession(response);
      setCountdown(response.otpValiditySeconds || 120);
      setOtp('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  }, [countdown, identifier, tenantSlug]);

  // Loading state
  if (tenantLoading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">{t.common.loading}</p>
      </main>
    );
  }

  // Error state - tenant not found
  if (tenantError || !tenant) {
    return (
      <main className="ds-container">
        <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 16px' }}>
          <Card>
            <div className="ds-alert ds-alert--error" role="alert">
              {tenantError || 'Organization not found'}
            </div>
            <p className="ds-text-muted" style={{ marginTop: 16 }}>
              {t.portal?.checkUrlOrContact || 'Please check the URL or contact your organization.'}
            </p>
            <div style={{ marginTop: 16 }}>
              <Link to="/my-consent" style={{ color: 'var(--ds-accent)' }}>
                {t.portal?.findMyOrganization || 'Find my organization'}
              </Link>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.mainContainer} role="main" aria-labelledby="portal-title">
      {/* Skip to main content link for keyboard users */}
      <a 
        href="#login-form" 
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.left = '10px';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.padding = '12px 24px';
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.color = 'var(--tashi-blue-grey-9)';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.currentTarget.style.position = 'absolute';
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to login form
      </a>

      {/* Top bar with language selector */}
      <nav style={styles.topBar} aria-label="Language selection">
        <div style={{ 
          display: 'flex', 
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>
          <span id="language-label" className="sr-only">Select language</span>
          {/* Show all languages in a dropdown on mobile, buttons on desktop */}
          {isLargeScreen ? (
            // Desktop: Show all language buttons
            <div role="group" aria-labelledby="language-label" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {supportedLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  aria-pressed={language === lang.code}
                  aria-label={`Select ${lang.name} language`}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '2px solid transparent',
                    background: language === lang.code ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 14, // WCAG: minimum 14px for UI controls
                    fontWeight: language === lang.code ? 600 : 500,
                    transition: 'all 0.2s',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.8)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,255,255,0.3)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {lang.nativeName}
                </button>
              ))}
            </div>
          ) : (
            // Mobile: Show dropdown
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as any)}
              aria-label="Select language"
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                border: '2px solid transparent',
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 16, // WCAG: 16px for form controls
                outline: 'none',
              }}
            >
              {supportedLanguages.map((lang) => (
                <option key={lang.code} value={lang.code} style={{ color: '#333' }}>
                  {lang.nativeName}
                </option>
              ))}
            </select>
          )}
        </div>
      </nav>

      {/* Main content area */}
      <div style={styles.contentArea}>
        <div style={{
          ...styles.twoColumnLayout,
          gridTemplateColumns: isLargeScreen ? '1fr 480px' : '1fr',
        }}>
          
          {/* Hero section - only visible on large screens */}
          {isLargeScreen && (
            <div style={{ ...styles.heroSection, display: 'flex' }}>
              <div style={{ marginBottom: 16 }}>
                {tenant.logo ? (
                  <img 
                    src={tenant.logo} 
                    alt={`${tenant.name} logo`} 
                    style={{ height: 64, filter: 'brightness(0) invert(1)', marginBottom: 8 }}
                  />
                ) : (
                  <div style={{ 
                    width: 64, height: 64, 
                    background: 'rgba(255,255,255,0.2)', 
                    borderRadius: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    marginBottom: 8,
                  }}>🔐</div>
                )}
              </div>
              <h1 style={{ fontSize: 38, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.2 }}>
                {tenant.name}
              </h1>
              <p style={{ fontSize: 18, margin: '0 0 8px', opacity: 0.95, fontWeight: 500 }}>
                {t.portal?.myConsents || 'My Consents'} Portal
              </p>
              <p style={{ fontSize: 16, margin: '0 0 28px', opacity: 0.85, lineHeight: 1.6 }}>
                {t.portal?.viewManageConsents || 'View and manage your consent artifacts'}
              </p>
              
              {/* What you can do section */}
              <section 
                aria-labelledby="features-heading"
                style={{ 
                  padding: '20px 24px', 
                  background: 'rgba(255,255,255,0.1)', 
                  borderRadius: 12,
                  backdropFilter: 'blur(8px)',
                  marginBottom: 16,
                }}
              >
                <h2 id="features-heading" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, opacity: 0.95 }}>
                  What you can do here:
                </h2>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span aria-hidden="true" style={{ 
                      width: 32, height: 32, 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                    }}>📋</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 500 }}>View Consent History</div>
                      <div style={{ fontSize: 14, opacity: 0.85 }}>See all consents you have provided</div>
                    </div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span aria-hidden="true" style={{ 
                      width: 32, height: 32, 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                    }}>✋</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 500 }}>Withdraw Consent</div>
                      <div style={{ fontSize: 14, opacity: 0.85 }}>Request to withdraw any active consent</div>
                    </div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span aria-hidden="true" style={{ 
                      width: 32, height: 32, 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                    }}>📊</span>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 500 }}>Track Requests</div>
                      <div style={{ fontSize: 14, opacity: 0.85 }}>Monitor status of your requests</div>
                    </div>
                  </li>
                </ul>
              </section>

              {/* Security note */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10,
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 8,
              }}>
                <span aria-hidden="true" style={{ fontSize: 18 }}>🔒</span>
                <span style={{ fontSize: 14, opacity: 0.9 }}>
                  Secure 3-step verification protects your account
                </span>
              </div>
            </div>
          )}

          {/* Login card */}
          <div style={styles.loginCardContainer}>
            <Card style={{ padding: isLargeScreen ? 32 : 24 }}>
              {/* Header with tenant branding - only on mobile */}
              {!isLargeScreen && (
                <header style={{ textAlign: 'center', marginBottom: 20 }}>
                  {tenant.logo && (
                    <img 
                      src={tenant.logo} 
                      alt={`${tenant.name} logo`} 
                      style={{ height: 48, marginBottom: 12 }}
                    />
                  )}
                  <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>
                    {tenant.name}
                  </h1>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-secondary)' }}>
                    {t.portal?.myConsents || 'My Consents'} Portal
                  </p>
                </header>
              )}

              {/* On desktop, show tenant name in card header */}
              {isLargeScreen && (
                <header style={{ textAlign: 'center', marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px', color: 'var(--tashi-blue-grey-9)' }}>
                    {t.portal?.login || 'Login'}
                  </h2>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-secondary)' }}>
                    {tenant.name}
                  </p>
                </header>
              )}

              {/* Progress indicator - accessible step indicator */}
              <nav aria-label="Login progress" style={{ marginBottom: 24 }}>
                <ol style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 8,
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                }}>
                  {['identifier', 'otp', 'security'].map((s, index) => {
                    const stepNames = ['Enter contact', 'Verify OTP', 'Security question'];
                    const isCompleted = ['identifier', 'otp', 'security'].indexOf(step) > index;
                    const isCurrent = step === s;
                    return (
                      <li
                        key={s}
                        aria-current={isCurrent ? 'step' : undefined}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isCurrent ? 'var(--tashi-blue-grey-9)' : 
                                     isCompleted ? 'var(--ds-success)' : 'var(--ds-bg-secondary)',
                          color: isCurrent || isCompleted ? 'white' : 'var(--ds-text-muted)',
                          fontWeight: 600,
                          fontSize: 14,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <span className="sr-only">{stepNames[index]}: {isCompleted ? 'completed' : isCurrent ? 'current' : 'not started'}</span>
                        <span aria-hidden="true">{isCompleted ? '✓' : index + 1}</span>
                      </li>
                    );
                  })}
                </ol>
              </nav>

              {/* Error message - aria-live for screen readers */}
              <div aria-live="polite" aria-atomic="true">
                {error && (
                  <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
                    {error}
                  </div>
                )}
              </div>

              {/* Step 1: Phone/Email Entry */}
              {step === 'identifier' && (
                <form onSubmit={handleIdentifierSubmit} noValidate id="login-form">
                  <div className="ds-field" style={{ marginBottom: 20 }}>
                    <Label htmlFor="identifier" style={{ fontSize: 16, marginBottom: 8 }}>
                      {t.portal?.phoneOrEmail || 'Phone Number or Email'}
                    </Label>
                    <Input
                      type="text"
                      id="identifier"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder={t.portal?.enterPhoneOrEmailPlaceholder || 'Enter your registered phone or email'}
                      aria-describedby="identifier-help"
                      style={{ 
                        fontSize: 16, 
                        padding: '14px',
                        height: 52,
                      }}
                      autoFocus
                      autoComplete="tel email"
                    />
                    <p id="identifier-help" className="ds-text-muted" style={{ fontSize: 14, marginTop: 8 }}>
                      {t.portal?.otpWillBeSent || 'We will send you a one-time password (OTP)'}
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    isLoading={loading} 
                    disabled={loading}
                    style={{ 
                      width: '100%', 
                      padding: '14px',
                      fontSize: 16,
                      height: 52,
                    }}
                  >
                    {loading ? t.common.loading : (t.portal?.sendOtp || 'Send OTP')}
                  </Button>
                </form>
              )}

              {/* Step 2: OTP Verification */}
              {step === 'otp' && loginSession && (
                <form onSubmit={handleOtpSubmit} noValidate id="login-form">
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <p style={{ fontSize: 16, marginBottom: 8 }}>
                      {t.portal?.otpSentTo || 'OTP sent to'}: <strong>{loginSession.maskedIdentifier}</strong>
                    </p>
                    {countdown > 0 && (
                      <p aria-live="polite" className="ds-text-muted" style={{ fontSize: 14 }}>
                        {t.portal?.expiresIn || 'Expires in'}: <span aria-label={`${Math.floor(countdown / 60)} minutes ${countdown % 60} seconds`}>{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</span>
                      </p>
                    )}
                  </div>

                  <div className="ds-field" style={{ marginBottom: 20 }}>
                    <Label htmlFor="otp" style={{ fontSize: 16, marginBottom: 8 }}>
                      {t.portal?.enterOtp || 'Enter OTP'}
                    </Label>
                    <Input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="• • • • • •"
                      aria-describedby="otp-help"
                      style={{ 
                        fontSize: 24, 
                        padding: '14px',
                        height: 52,
                        textAlign: 'center',
                        letterSpacing: 8,
                      }}
                      autoFocus
                      inputMode="numeric"
                      maxLength={6}
                    />
                    <p id="otp-help" className="sr-only">Enter the 6-digit code sent to your phone or email</p>
                  </div>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    isLoading={loading} 
                    disabled={loading}
                    style={{ 
                      width: '100%', 
                      padding: '14px',
                      fontSize: 16,
                      height: 52,
                      marginBottom: 12,
                    }}
                  >
                    {loading ? t.common.loading : (t.portal?.verifyOtp || 'Verify OTP')}
                  </Button>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setStep('identifier')}
                      style={{
                        background: 'none',
                        border: '2px solid transparent',
                        color: 'var(--ds-accent)',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 8,
                        borderRadius: 4,
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--ds-accent)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    >
                      ← {t.common.back}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={countdown > 0 || loading}
                      aria-disabled={countdown > 0 || loading}
                      style={{
                        background: 'none',
                        border: '2px solid transparent',
                        color: countdown > 0 ? 'var(--ds-text-muted)' : 'var(--ds-accent)',
                        cursor: countdown > 0 ? 'not-allowed' : 'pointer',
                        fontSize: 14,
                        padding: 8,
                        borderRadius: 4,
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = countdown > 0 ? 'transparent' : 'var(--ds-accent)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    >
                      {t.portal?.resendOtp || 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}

              {/* Step 3: Security Question */}
              {step === 'security' && securityQuestion && (
                <form onSubmit={handleSecuritySubmit} noValidate id="login-form">
                  <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{
                      background: 'var(--ds-success-light)',
                      color: 'var(--ds-success)',
                      padding: '8px 16px',
                      borderRadius: 8,
                      display: 'inline-block',
                      marginBottom: 16,
                      fontSize: 14,
                    }}>
                      ✓ {t.portal?.otpVerified || 'OTP Verified'}
                    </div>
                    <p style={{ fontSize: 16, marginBottom: 4 }}>
                      {t.portal?.oneMoreStep || 'One more step to verify your identity'}
                    </p>
                  </div>

                  <div className="ds-field" style={{ marginBottom: 20 }}>
                    <Label htmlFor="securityAnswer" style={{ fontSize: 16, marginBottom: 8 }}>
                      {securityQuestion.questionLabel}
                    </Label>
                    <Input
                      type="text"
                      id="securityAnswer"
                      value={securityAnswer}
                      onChange={(e) => setSecurityAnswer(e.target.value)}
                      placeholder={t.portal?.enterAnswer || 'Enter your answer'}
                      style={{ 
                        fontSize: 16, 
                        padding: '14px',
                        height: 52,
                        textAlign: 'center',
                      }}
                      autoFocus
                      inputMode="text"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    isLoading={loading} 
                    disabled={loading}
                    style={{ 
                      width: '100%', 
                      padding: '14px',
                      fontSize: 16,
                      height: 52,
                      marginBottom: 12,
                    }}
                  >
                    {loading ? t.common.loading : (t.portal?.login || 'Login')}
                  </Button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep('identifier');
                      setOtp('');
                      setSecurityAnswer('');
                      setLoginSession(null);
                      setSecurityQuestion(null);
                    }}
                    style={{
                      width: '100%',
                      background: 'none',
                      border: '2px solid transparent',
                      color: 'var(--ds-accent)',
                      cursor: 'pointer',
                      fontSize: 14,
                      padding: 12,
                      borderRadius: 4,
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'var(--ds-accent)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    {t.portal?.startOver || 'Start over'}
                  </button>
                </form>
              )}

              {/* Help link */}
              <footer style={{ textAlign: 'center', marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--ds-border)' }}>
                <Link 
                  to={`/t/${tenantSlug}/my-consent/help`} 
                  style={{ 
                    color: 'var(--ds-accent)', 
                    fontSize: 14,
                    textDecoration: 'none',
                  }}
                >
                  {t.portal?.needHelp || 'Need help?'}
                </Link>
              </footer>
            </Card>

            {/* Info section - mobile only */}
            {!isLargeScreen && (
              <section 
                aria-label="Portal features"
                style={{ 
                  marginTop: 20,
                  padding: '16px 20px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <p style={{ 
                  margin: '0 0 12px', 
                  fontSize: 14, 
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: 500,
                }}>
                  This portal allows you to:
                </p>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: 20, 
                  fontSize: 14, 
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.8,
                }}>
                  <li>View your consent history</li>
                  <li>Request to withdraw consent</li>
                  <li>Track your request status</li>
                </ul>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                }}>
                  <span aria-hidden="true" style={{ fontSize: 14 }}>🔒</span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
                    Secure 3-step verification
                  </span>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default DataOwnerPortalLogin;
